import React, { useEffect, useState } from "react";
import { cn } from "../../lib/utils";

interface AnalogClockProps {
  timezone?: string;
  size?: number;
  showTimezone?: boolean;
}

const AnalogClock: React.FC<AnalogClockProps> = ({
  timezone = "America/New_York",
  size = 80,
  showTimezone = true,
}) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get time in specified timezone
  const getTimeInTimezone = () => {
    return new Date(
      time.toLocaleString("en-US", {
        timeZone: timezone,
      })
    );
  };

  const currentTime = getTimeInTimezone();
  const hours = currentTime.getHours() % 12;
  const minutes = currentTime.getMinutes();
  const seconds = currentTime.getSeconds();

  // Calculate rotation angles
  const secondAngle = (seconds * 6) - 90; // 6 degrees per second
  const minuteAngle = (minutes * 6 + seconds * 0.1) - 90; // 6 degrees per minute
  const hourAngle = (hours * 30 + minutes * 0.5) - 90; // 30 degrees per hour

  // Get timezone abbreviation
  const getTimezoneAbbr = () => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(time);
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    return tzPart?.value || "EDT";
  };

  // Calculate time difference from user's timezone
  const getTimeDifference = () => {
    const userTime = new Date();
    const nycTime = getTimeInTimezone();
    const diffMs = nycTime.getTime() - userTime.getTime();
    const diffHrs = Math.round(diffMs / (1000 * 60 * 60));

    if (diffHrs === 0) return "";
    return diffHrs > 0 ? `+${diffHrs}` : `${diffHrs}`;
  };

  // Format digital time
  const formatDigitalTime = () => {
    const hrs = currentTime.getHours();
    const mins = currentTime.getMinutes().toString().padStart(2, '0');
    const ampm = hrs >= 12 ? 'PM' : 'AM';
    const displayHrs = hrs % 12 || 12;
    return `${displayHrs}:${mins} ${ampm}`;
  };

  // Check availability (8 AM - 12 AM EDT)
  const isAvailable = () => {
    const hours = currentTime.getHours();
    return hours >= 8 && hours < 24; // 8 AM to 11:59 PM
  };

  const available = isAvailable();
  const availabilityMessage = available
    ? "Looks like Aditya's online! Expect a speedy response :)"
    : "Oh snaps, Aditya's catching some Z's! Send a text anyway. He'll read it and get back to you once he's up.";

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Digital Time */}
      <div className="text-sm font-medium footer-clock-text" style={{ textAlign: 'center', width: '100%' }}>
        {formatDigitalTime()}
      </div>

      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        className={cn("transition-all duration-300")}
        style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
      >
        {/* Clock face */}
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="transparent"
          className="footer-clock-border"
          strokeWidth="1"
        />

        {/* Hour markers */}
        {[...Array(12)].map((_, i) => {
          const angle = (i * 30 - 90) * (Math.PI / 180);
          const x1 = 50 + 40 * Math.cos(angle);
          const y1 = 50 + 40 * Math.sin(angle);
          const x2 = 50 + 44 * Math.cos(angle);
          const y2 = 50 + 44 * Math.sin(angle);

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className="footer-clock-marker"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          );
        })}

        {/* Hour hand */}
        <line
          x1="50"
          y1="50"
          x2={50 + 25 * Math.cos(hourAngle * (Math.PI / 180))}
          y2={50 + 25 * Math.sin(hourAngle * (Math.PI / 180))}
          className="footer-clock-hand"
          strokeWidth="3"
          strokeLinecap="round"
        />

        {/* Minute hand */}
        <line
          x1="50"
          y1="50"
          x2={50 + 35 * Math.cos(minuteAngle * (Math.PI / 180))}
          y2={50 + 35 * Math.sin(minuteAngle * (Math.PI / 180))}
          className="footer-clock-hand"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Second hand */}
        <line
          x1="50"
          y1="50"
          x2={50 + 38 * Math.cos(secondAngle * (Math.PI / 180))}
          y2={50 + 38 * Math.sin(secondAngle * (Math.PI / 180))}
          className="footer-clock-second"
          strokeWidth="1"
          strokeLinecap="round"
        />

        {/* Center dot */}
        <circle
          cx="50"
          cy="50"
          r="2"
          className="footer-clock-center"
        />
      </svg>

      {/* Timezone label */}
      {showTimezone && (
        <div className="text-center">
          <div className="text-xs font-medium footer-clock-text flex items-center justify-center gap-1.5">
            <span
              className="availability-dot"
              data-available={available}
              title={availabilityMessage}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: available ? '#22c55e' : '#ef4444',
                display: 'inline-block',
                cursor: 'help',
                transition: 'all 0.3s ease',
                boxShadow: available
                  ? '0 0 8px rgba(34, 197, 94, 0.6)'
                  : '0 0 8px rgba(239, 68, 68, 0.6)',
              }}
            />
            New York, {getTimezoneAbbr()}
          </div>
          {getTimeDifference() && (
            <div className="text-xs font-normal footer-clock-text opacity-70">
              ({getTimeDifference()} hrs)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalogClock;
